import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Building2,
  FileText,
  Settings,
  Shield,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { BostonFederalName } from '../components/RegisteredName';
import Editable from '../cms/Editable';

const heroServices = [
  { icon: Shield, title: 'Financial Control', detail: 'Discipline at every layer' },
  { icon: BarChart3, title: 'Data & Visibility', detail: 'Real-time insights' },
  { icon: Settings, title: 'Automation', detail: 'Smarter operations' },
  { icon: Users, title: 'Compliance', detail: 'Built-in. Always.' },
  { icon: Building2, title: 'Capital Ready', detail: 'Stronger. Faster.' },
];

const serviceCards = [
  {
    icon: Shield,
    title: 'Financial Control',
    desc: 'Strong systems, oversight, and discipline that protect your business.',
    to: '/capabilities',
  },
  {
    icon: TrendingUp,
    title: 'Strategic Planning',
    desc: 'Budgets, forecasts, and scenario planning that drive better decisions.',
    to: '/capabilities',
  },
  {
    icon: FileText,
    title: 'Accounting Operations',
    desc: 'Day-to-day financial management handled with precision.',
    to: '/capabilities',
  },
  {
    icon: BarChart3,
    title: 'Reporting & Visibility',
    desc: 'Timely, accurate reporting that gives you a clear view of performance.',
    to: '/capabilities',
  },
  {
    icon: Settings,
    title: 'Automation',
    desc: 'Intelligent workflows that reduce manual work and improve accuracy.',
    to: '/innovation',
  },
  {
    icon: Users,
    title: 'Payroll & Compliance',
    desc: 'Complex payroll handled with expertise and total compliance.',
    to: '/payroll-compliance',
  },
  {
    icon: Building2,
    title: 'Bankability',
    desc: 'Stronger financials. Stronger relationships. More opportunities.',
    to: '/capital-partners',
  },
];

export default function Home() {
  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-[#061E35] text-white flex flex-col lg:min-h-[520px] xl:min-h-[560px]" data-testid="home-hero">
        <div
          className="bosfed-hero-bg absolute inset-0 bg-no-repeat"
          style={{
            backgroundImage: "url('/assets/bosfed-boston-ai-hero-analytics-sharp.jpg')",
            filter: 'brightness(1.01) saturate(1.04) contrast(0.99)',
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(90deg,rgba(6,30,53,0.94)_0%,rgba(6,30,53,0.88)_16%,rgba(6,30,53,0.74)_32%,rgba(6,30,53,0.52)_46%,rgba(6,30,53,0.30)_58%,rgba(6,30,53,0.12)_70%,rgba(6,30,53,0)_84%)]"
          aria-hidden="true"
        />
        <div
          className="absolute inset-x-0 bottom-0 h-28 bg-[linear-gradient(0deg,rgba(6,30,53,0.72)_0%,rgba(6,30,53,0)_100%)]"
          aria-hidden="true"
        />

        {/* Subtle data/tech graphics filling the dark left area */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 1000 600"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
          aria-hidden="true"
        >
          {/* faint grid texture only — kept well clear of the headline */}
          <g stroke="#36BDF2" strokeWidth="0.5" opacity="0.05">
            <path d="M0 120 H520 M0 220 H520 M0 320 H520 M0 420 H520 M0 520 H520" />
            <path d="M80 0 V600 M180 0 V600 M280 0 V600 M380 0 V600 M480 0 V600" />
          </g>
          {/* faint bar chart accent, bottom-left corner */}
          <g fill="#36BDF2" opacity="0.10">
            <rect x="40" y="520" width="14" height="60" />
            <rect x="66" y="498" width="14" height="82" />
            <rect x="92" y="534" width="14" height="46" />
            <rect x="118" y="484" width="14" height="96" />
            <rect x="144" y="514" width="14" height="66" />
          </g>
        </svg>

        <div className="relative w-full max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 pt-14 pb-5 lg:pt-20 lg:pb-8">
          <div className="max-w-[960px]">
            <Editable
              id="home.hero.eyebrow"
              as="p"
              page="Home"
              label="Hero eyebrow"
              className="text-[#36BDF2] text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.14em] mb-5 mt-3"
            >
              Financial Control. Intelligent Systems. Better Outcomes.
            </Editable>
            <h1
              className="text-[34px] sm:text-[44px] lg:text-[52px] xl:text-[56px] font-bold leading-[1.07] tracking-tight mb-4 max-w-[860px]"
              style={{ textShadow: '0 2px 18px rgba(6,30,53,0.55)' }}
            >
              <Editable id="home.hero.headline" as="span" page="Home" label="Hero headline (line 1)">
                We build the financial infrastructure that
              </Editable>
              <Editable
                id="home.hero.headline2"
                as="span"
                page="Home"
                label="Hero headline (line 2)"
                className="block text-[#36BDF2]"
              >
                moves businesses forward.
              </Editable>
            </h1>
            <p className="text-[15px] sm:text-base text-white/88 leading-relaxed max-w-[620px] mb-5">
              <BostonFederalName />{' '}
              <Editable id="home.hero.subtext" as="span" page="Home" label="Hero paragraph" multiline>
                is a hands-on financial control and infrastructure firm that helps businesses gain
                visibility, strengthen control, stay compliant, and become capital-ready.
              </Editable>
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              <Link
                to="/capabilities"
                data-testid="hero-primary-cta"
                className="bg-[#1FA8F5] text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#0B8ED8] transition-colors inline-flex items-center justify-center gap-2"
              >
                Explore Capabilities <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-secondary-cta"
                className="border border-white/60 text-white px-6 py-3 rounded-md text-sm font-semibold hover:bg-white hover:text-[#061E35] transition-all inline-flex items-center justify-center gap-2"
              >
                Speak with an Expert <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>

        <div className="relative bg-[#061E35]/92">
          <div className="w-full max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
              {heroServices.map(({ icon: Icon, title, detail }, i) => (
                <div key={title} className="flex items-center gap-3 py-4 lg:px-5 xl:px-7 first:pl-0">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} strokeWidth={1.55} className="text-[#36BDF2]" />
                  </div>
                  <div>
                    <Editable id={`home.heroservice.${i}.title`} as="h2" page="Home" label={`Hero strip ${i + 1} — title`} className="text-[9px] xl:text-[10px] font-bold uppercase tracking-[0.12em] text-white whitespace-nowrap">{title}</Editable>
                    <Editable id={`home.heroservice.${i}.detail`} as="p" page="Home" label={`Hero strip ${i + 1} — detail`} className="text-[11px] xl:text-xs text-white/72 mt-1">{detail}</Editable>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-5 lg:py-6" data-testid="what-we-do">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-3xl mx-auto text-center mb-7">
            <Editable id="home.whatwedo.eyebrow" as="p" page="Home" label="What We Do — eyebrow" className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.32em] mb-3">What We Do</Editable>
            <Editable id="home.whatwedo.heading" as="h2" page="Home" label="What We Do — heading" className="text-2xl sm:text-[30px] font-bold text-[#0B1F33] leading-tight">
              Integrated. Hands-On. Built for Growth.
            </Editable>
            <Editable id="home.whatwedo.subtitle" as="p" page="Home" label="What We Do — subtitle" multiline className="text-[#40516A] text-sm leading-relaxed mt-2">
              We combine deep financial expertise with modern systems and proprietary tools
              to deliver clarity, control, and confidence at every stage.
            </Editable>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
            {serviceCards.map(({ icon: Icon, title, desc, to }, i) => (
              <Link
                key={title}
                to={to}
                className="group rounded-md bg-white px-4 py-4 min-h-[152px] hover:shadow-[0_18px_45px_rgba(11,31,51,0.08)] transition-all"
              >
                <Icon size={23} strokeWidth={1.55} className="text-[#1FA8F5] mb-3" />
                <Editable id={`home.card.${i}.title`} as="h3" page="Home" label={`Service card ${i + 1} — title`} className="text-[#0B1F33] font-bold text-[13px] leading-tight mb-2">{title}</Editable>
                <Editable id={`home.card.${i}.desc`} as="p" page="Home" label={`Service card ${i + 1} — description`} multiline className="text-[#40516A] text-[11px] leading-relaxed">{desc}</Editable>
                <span className="text-[#1FA8F5] text-[11px] font-bold mt-4 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn more <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
